import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import {
  BACKFILL_UNAVAILABLE_CODE,
  shareColumnBackfill,
} from "../../../utils/requests/shareColumnBackfill";
import logger from "../../../lib/logger";
import type { LinkedBoard } from "../table.data.interface";
import type { ColumnHolder, ColumnIdentity } from "./shareColumnEligibility";

export interface ShareColumnRequest {
  identity: ColumnIdentity;
  /** The title to create under. Differs from identity.title when the user renamed it. */
  name: string;
  holders: ColumnHolder[];
  source: ColumnHolder;
  targets: LinkedBoard[];
  token: string;
}

export interface BoardFailure {
  board: LinkedBoard;
  message: string;
}

export type ValueCopy =
  /** Every connected item's value is being copied in the background, reported over the socket. */
  | { status: "queued"; requestId: string; columnKey: string; itemCount: number }
  /** No item on the new boards is connected to one on a board that has the column. */
  | { status: "none" }
  /** The backend can't copy values right now (orchestration force push is off). */
  | { status: "unavailable" }
  | { status: "failed"; message: string };

/**
 * Roughly how many values monday lets one account write per minute: every mutation costs a flat
 * 30k complexity against a 20M-per-minute budget shared with reads (measured 2026-08-18).
 */
export const BACKFILL_WRITES_PER_MINUTE = 600;

/** Above this many items the dialog says how long the copy will take. */
export const LARGE_BACKFILL_ITEM_COUNT = 500;

export const estimateBackfillMinutes = (itemCount: number): number =>
  Math.max(1, Math.ceil(itemCount / BACKFILL_WRITES_PER_MINUTE));

/** The boards to copy from, the one the user clicked from first, each with its own column id. */
export const backfillSourceBoards = (source: ColumnHolder, holders: ColumnHolder[]) => [
  { boardId: source.board.boardId, columnId: source.column.id },
  ...holders
    .filter((holder) => holder.board.boardId !== source.board.boardId)
    .map((holder) => ({ boardId: holder.board.boardId, columnId: holder.column.id })),
];

export interface ShareColumnResult {
  name: string;
  created: LinkedBoard[];
  failed: BoardFailure[];
  /** Set when a rename was needed and did not fully apply. Nothing is created in that case. */
  renameError: string | null;
  valueCopy: ValueCopy;
}

const settingsOf = (settings: any) => {
  if (typeof settings !== "string") return settings ?? undefined;
  try {
    return JSON.parse(settings);
  } catch {
    return undefined;
  }
};

/**
 * Copies every connected item's value into the new columns. The backend finds the items on the
 * new boards that share a sync group with an item on a board holding the column, and queues one
 * orchestration job per item; each reads its source's live value, so cells that were never
 * edited copy too.
 */
const copyValues = async (
  { source, holders, identity, token }: ShareColumnRequest,
  name: string,
  created: LinkedBoard[],
): Promise<ValueCopy> => {
  try {
    const response = await shareColumnBackfill(token, {
      columnTitle: name,
      columnType: identity.type,
      sourceBoards: backfillSourceBoards(source, holders),
      targetBoardIds: created.map((board) => board.boardId),
    });

    if (!response?.requestId || !response.itemCount) return { status: "none" };

    return {
      status: "queued",
      requestId: response.requestId,
      columnKey: response.columnKey ?? `${identity.type}_${name}`,
      itemCount: response.itemCount,
    };
  } catch (error: any) {
    if (error?.response?.data?.code === BACKFILL_UNAVAILABLE_CODE) return { status: "unavailable" };

    logger.error("Share column: value backfill failed", {
      message: error?.message,
      columnTitle: name,
      columnType: identity.type,
    });
    return {
      status: "failed",
      message: error?.response?.data?.error_message ?? error?.message ?? "Unknown error",
    };
  }
};

/**
 * Renames the column where it already lives (only when the user changed the name), creates it
 * on each target board, then copies the values of every connected item across. Boards are handled one at a
 * time: each create_column is a full-price mutation against the account's complexity budget.
 */
export const shareColumn = async (request: ShareColumnRequest): Promise<ShareColumnResult> => {
  const { identity, holders, source, targets } = request;
  const name = request.name.trim();
  const result: ShareColumnResult = {
    name,
    created: [],
    failed: [],
    renameError: null,
    valueCopy: { status: "none" },
  };

  if (name !== identity.title) {
    // Every holder is renamed, not just the source: they sync by name, so renaming one alone
    // would silently unshare the column from the rest.
    for (const { board, column } of holders) {
      try {
        await MondayApiQueryInstance.renameColumn(parseInt(board.boardId, 10), column.id, name);
      } catch (error: any) {
        result.renameError =
          `Couldn't rename the column on ${board.boardName}. ${error?.message ?? ""}`.trim();
        return result;
      }
    }
  }

  const labels = settingsOf(source.column.settings)?.labels;
  for (const board of targets) {
    try {
      const response = await MondayApiQueryInstance.createColumn(
        parseInt(board.boardId, 10),
        name,
        identity.type,
        labels,
      );
      if (!response?.data?.create_column?.id)
        throw new Error("monday did not return the new column");

      result.created.push(board);
    } catch (error: any) {
      result.failed.push({ board, message: error?.message ?? "Unknown error" });
    }
  }

  if (result.created.length > 0) {
    result.valueCopy = await copyValues(request, name, result.created);
  }

  return result;
};
